import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import {
  ChartBarIcon, UsersIcon, ArrowUpIcon, ArrowDownIcon,
  Calendar, BarChart3, PieChart as PieChartIcon, X, Lock, Send, Info,
  AlertCircle, CheckCircle2, Check, Clock, Clock2, UserPlus, PlayCircle,
  Rocket, Trophy,
  RocketIcon,
  Mail,
  Users,
  Gift,
  Search,
  Play,
  Plus,
  TrendingUp
} from 'lucide-react';
import { getDashboardAnalytics, getUserCompany, getSellerRecommendationsForDashboard, getSellerRecommendationsSent, getSellerRecommendationsHide, addSellerRecommendationStatus, checkSubscriptionStatus, createBuyerDemo, publishCompany, checkHasActiveLaunch, getCompanyLaunches, getLeadCardsApproved, getLeadFormSubmissionsForDashboard, deleteLeadFormSubmission, createSellerDemo, getCompanyOffer } from '../../utils/api';
import { exportToCSV } from '../../utils/csvExport';
import useSubscriberStatus from '../../hooks/useSubscriberStatus';
import useCountUp from '../../hooks/useCountUp';
import { FaEnvelope } from 'react-icons/fa';
import WelcomeDialog from '../../components/WelcomeDialog';
import ScrollToTop from '../../components/layout/ScrollToTop';

// Sample data for charts
const monthlyLeadData = [
  { name: 'Jan', value: 42, year: 2024 },
  { name: 'Feb', value: 53, year: 2024 },
  { name: 'Mar', value: 64, year: 2024 },
  { name: 'Apr', value: 89, year: 2024 },
  { name: 'May', value: 76, year: 2024 },
  { name: 'Jun', value: 98, year: 2024 },
  { name: 'Jul', value: 112, year: 2024 },
  { name: 'Aug', value: 86, year: 2024 },
  { name: 'Sep', value: 107, year: 2024 },
  { name: 'Oct', value: 94, year: 2024 },
  { name: 'Nov', value: 113, year: 2024 },
  { name: 'Dec', value: 126, year: 2024 },
];

const leadsBySource = [
  { name: 'Search', value: 45 },
  { name: 'Social', value: 25 },
  { name: 'Email', value: 15 },
  { name: 'Referral', value: 15 },
];

const COLORS = ['#8B5CF6', '#D946EF', '#F97316', '#0EA5E9'];

const topLeadsList = [
  { id: 1, name: 'Sarah Chen', company: 'Tech Solutions Inc.', status: 'Hot', source: 'Search', date: '2025-04-01' },
  { id: 2, name: 'David Rodriguez', company: 'Global Innovations', status: 'Warm', source: 'Social', date: '2025-04-03' },
  { id: 3, name: 'Jennifer Smith', company: 'Future Systems', status: 'Hot', source: 'Referral', date: '2025-04-05' },
  { id: 4, name: 'Michael Johnson', company: 'Swift Technologies', status: 'Cold', source: 'Email', date: '2025-04-08' },
  { id: 5, name: 'Emily Davis', company: 'Bright Horizons', status: 'Warm', source: 'Search', date: '2025-04-10' },
];

const newsletterData = [
  {
    id: 1,
    date: '2024-04-15',
    name: 'April Product Showcase',
    totalReach: 15420,
    views: 4231,
    clicks: 892,
    ctr: '21.1%'
  },
  {
    id: 2,
    date: '2024-04-01',
    name: 'March Product Roundup',
    totalReach: 14850,
    views: 3982,
    clicks: 756,
    ctr: '19.0%'
  },
  {
    id: 3,
    date: '2024-03-15',
    name: 'Spring Innovation Special',
    totalReach: 13200,
    views: 3654,
    clicks: 701,
    ctr: '19.2%'
  }
];

const newsletterSourceData = [
  { name: 'Weekly Newsletter', value: 45 },
  { name: 'Product Directory', value: 25 },
  { name: 'Featured Section', value: 20 },
  { name: 'Category Showcase', value: 10 }
];

const ITEMS_PER_PAGE = 10;

const ActiveLaunchBanner = ({ launches }) => {
  if (!launches || launches.length === 0) return null;

  return (
    <div className="mb-2 overflow-hidden">
      <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 rounded-lg shadow-lg">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-white/10 rounded-full p-3">
              <Rocket className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-white font-semibold text-lg">Active Launch in Progress</h3>
                <Trophy className="h-4 w-4 text-yellow-300" />
              </div>
              <p className="text-white/80 text-sm mt-1">
                Your product is currently scheduled for a weekly launch.
              </p>
            </div>
          </div>
          <Link
            to={`/dashboard/launch/manage/${launches[0].company_launch_id}`}
            className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Calendar className="h-4 w-4" />
            <span>Manage</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

const UpgradeDialog = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl w-full max-w-lg mx-4 p-8">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 rounded-lg p-3">
              <Search className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Unlock Prospect Search</h3>
              <p className="text-sm text-gray-500 mt-1">Find your ideal customers</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-purple-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-purple-900 mb-3">Why Upgrade?</h4>
          <ul className="space-y-2">
            <li className="flex items-center text-sm text-purple-800">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mr-2"></div>
              Access our database of qualified B2B buyers
            </li>
            <li className="flex items-center text-sm text-purple-800">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mr-2"></div>
              Filter by industry, company size, and technology stack
            </li>
            <li className="flex items-center text-sm text-purple-800">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mr-2"></div>
              Get real-time notifications when prospects view your profile
            </li>
            <li className="flex items-center text-sm text-purple-800">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mr-2"></div>
              Advanced analytics and engagement tracking
            </li>
          </ul>
        </div>

        <div className="flex items-center justify-end space-x-4">
          <button
            onClick={() => navigate('/buyer-directory')}
            className="px-4 py-2 text-gray-600 hover:text-gray-700 font-medium"
          >
            Preview
          </button>
          <button
            onClick={() => {
              navigate('/subscription');
              onClose();
            }}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-md"
          >
            Upgrade to Pro →
          </button>
        </div>
      </div>
    </div>
  );
};

const PendingDemoBanner = ({ count }) => {
  if (!count) return null;

  return (
    <div className="mb-2 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 rounded-lg shadow-lg">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-white/10 rounded-full p-3">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-white font-semibold text-lg">Pending Demo Request{count > 1 ? 's' : ''}</h3>
              </div>
              <p className="text-white/80 text-sm mt-1">
                You have {count} pending demo request{count > 1 ? 's' : ''} to review
              </p>
            </div>
          </div>
          <Link
            to="/dashboard/seller"
            className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Calendar className="h-4 w-4" />
            <span>View Demos</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

const EnableSellerBanner = ({ onEnable, isPublishing, onDismiss }) => {
  return (
    <div className="mb-2 overflow-hidden animate-fade-in">
      <div className="bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-500 rounded-lg shadow-lg">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-white/10 rounded-full p-3">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Start Receiving Demo Requests</h3>
              <p className="text-white/80 text-sm mt-1">
                Start accepting demo requests and connect with potential buyers
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onEnable}
              disabled={isPublishing}
              className="bg-white/10 hover:bg-white/20 text-white px-6 py-2.5 rounded-lg flex items-center space-x-2 transition-all duration-150 disabled:opacity-50 hover:scale-105"
            >
              {isPublishing ? (
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              <span>Enable Now</span>
            </button>
            <button
              onClick={onDismiss}
              className="bg-white/10 hover:bg-white/20 text-white/80 hover:text-white px-3 py-2.5 rounded-lg transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardAnalytics = () => {
  const [selectedYear, setSelectedYear] = useState(2025);
  const [viewType, setViewType] = useState('monthly'); // Add this state
  const [isLeadDetailsOpen, setIsLeadDetailsOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [showOfferBanner, setShowOfferBanner] = useState(() => {
    let ls = localStorage.getItem('hideOfferBanner');
    console.log('ls', ls);
    if (ls == 'true') {
      return false;
    }

    console.log('aa');
    return true;
  });

  const handleDismissOffer = () => {
    setShowOfferBanner(false);
    localStorage.setItem('hideOfferBanner', 'true');
  };
  const [showSellerBanner, setShowSellerBanner] = useState(() => {
    return localStorage.getItem('hideSellerBanner') !== 'true';
  });
  const handleDismissSellerBanner = () => {
    setShowSellerBanner(false);
    localStorage.setItem('hideSellerBanner', 'true');
  };
  const [showWelcome, setShowWelcome] = useState(() => {
    return localStorage.getItem('welcomeShown') !== 'true';
  });

  const handleCloseWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem('welcomeShown', 'true');
  };

  // Add new state near other banner states
  const [showBoostBanner, setShowBoostBanner] = useState(() => {
    return localStorage.getItem('hideBoostBanner') !== 'true';
  });

  const handleDismissBoost = () => {
    setShowBoostBanner(false);
    localStorage.setItem('hideBoostBanner', 'true');
  };

  // Add new state near other banner states
  const [showSearchBanner, setShowSearchBanner] = useState(() => {
    return localStorage.getItem('hideSearchBanner') !== 'true';
  });

  const handleDismissSearch = () => {
    setShowSearchBanner(false);
    localStorage.setItem('hideSearchBanner', 'true');
  };

  // Add new state near other dialog states
  const [isProspectSearchDialogOpen, setIsProspectSearchDialogOpen] = useState(false);

  // Calculate stats
  const filteredMonthlyData = monthlyLeadData.filter(item => item.year === selectedYear);
  const totalLeadsThisMonth = filteredMonthlyData[11]?.value || 0;
  const prevMonthLeads = filteredMonthlyData[10]?.value || 0;
  const leadsChange = ((totalLeadsThisMonth - prevMonthLeads) / prevMonthLeads) * 100;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset();
    const adjustedDate = new Date(date.getTime() + offset * 60 * 1000);
    return adjustedDate.toLocaleDateString();
  };

  const [activeTab, setActiveTab] = React.useState('overview');
  const [analyticsData, setAnalyticsData] = React.useState(null);
  const [companyClicks, setCompanyClicks] = useState([]);
  const [companyViews, setCompanyViews] = useState([]);
  const imageUrl = process.env.REACT_APP_IMAGE_URL;

  const [topLeadsDisplayCount, setTopLeadsDisplayCount] = useState(ITEMS_PER_PAGE);
  const [companyClicksDisplayCount, setCompanyClicksDisplayCount] = useState(ITEMS_PER_PAGE);
  const [companyViewsDisplayCount, setCompanyViewsDisplayCount] = useState(ITEMS_PER_PAGE);

  const [recommendations, setRecommendations] = useState([]);
  const [recommendationsSent, setRecommendationsSent] = useState([]);
  const [recommendationsHide, setRecommendationsHide] = useState([]);

  const [loadedRecommendations, setLoadedRecommendations] = useState(false);
  const [availableCredits, setAvailableCredits] = useState(5); // Add this state
  const [activeLeadsTab, setActiveLeadsTab] = useState('available'); // Add new state for leads tab
  const [isCompanyViewsVisible, setIsCompanyViewsVisible] = useState(false); // Add this state
  const [recommendationsDisplayCount, setRecommendationsDisplayCount] = useState(ITEMS_PER_PAGE); // Add this state
  const [isCreditsInfoOpen, setIsCreditsInfoOpen] = useState(false); // Add new state for credits info dialog
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loadingNoFit, setLoadingNoFit] = useState(null);

  const displayedTopLeads = topLeadsList.slice(0, topLeadsDisplayCount);
  const displayedCompanyClicks = companyClicks.slice(0, companyClicksDisplayCount);
  const displayedCompanyViews = companyViews.slice(0, companyViewsDisplayCount);
  const [tableData, setTableData] = useState([]);
  const [tableDataDaily, setTableDataDaily] = useState([]);
  const [loading, setLoading] = useState(false);
  const { subscriberStatus, subscriberLoading, subscriberData, subscriberError, refetch } = useSubscriberStatus();
  const [hasSubscription, setHasSubscription] = useState(false);
  const [loadedSubscriptionCheck, setLoadedSubscriptionCheck] = useState(false);

  const animatedImpressions = useCountUp(analyticsData?.impressions || 0);
  const animatedPageViews = useCountUp(analyticsData?.page_views || 0);
  const animatedPageClicks = useCountUp(analyticsData?.page_clicks || 0);

  const [showProfileNudge, setShowProfileNudge] = useState(true);
  const [userCompany, setUserCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);

  const [isPublishing, setIsPublishing] = useState(false);
  const [publishComplete, setPublishComplete] = useState(false);

  const [hasActiveLaunch, setHasActiveLaunch] = useState(false);

  const [launches, setLaunches] = useState([]);
  const [loadingLaunches, setLoadingLaunches] = useState(true);

  const [leadCards, setLeadCards] = useState([]);
  const [loadedLeadCards, setLoadedLeadCards] = useState(false);

  const [hasOffer, setHasOffer] = useState(false);
  const [loadedOffer, setLoadedOffer] = useState(false);

  const navigate = useNavigate();

  const getInterestBadge = (count) => {
    if (count > 10) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 min-w-[100px]">High Interest</span>;
    } else if (count > 5) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 min-w-[100px]">Repeat Clicks</span>;
    } else if (count > 2) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 min-w-[100px]">Return Visitor</span>;
    }
    return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 min-w-[100px]">New</span>;
  };

  const handleLoadMore = (type) => {
    if (!hasSubscription) {
      setIsUpgradeDialogOpen(true);
      return;
    }

    switch (type) {
      case 'topLeads':
        setTopLeadsDisplayCount(prev => prev + ITEMS_PER_PAGE);
        break;
      case 'companyClicks':
        setCompanyClicksDisplayCount(prev => prev + ITEMS_PER_PAGE);
        break;
      case 'companyViews':
        setCompanyViewsDisplayCount(prev => prev + ITEMS_PER_PAGE);
        break;
      case 'recommendations':
        setRecommendationsDisplayCount(prev => prev + ITEMS_PER_PAGE);
        break;
    }
  };

  async function fetchRecommendations() {
    const response = await getSellerRecommendationsForDashboard();
    if (response.success) {
      setRecommendations(response.data);
      setLoadedRecommendations(true);
    } else {
      console.error('Failed to fetch recommendations:', response.message);
    }
  }

  async function fetchRecommendationsSent() {
    const response = await getSellerRecommendationsSent();
    if (response.success) {
      setRecommendationsSent(response.data);
      setLoadedRecommendations(true);
    } else {
      console.error('Failed to fetch recommendations:', response.message);
    }
  }

  async function fetchRecommendationsHide() {
    const response = await getSellerRecommendationsHide();
    if (response.success) {
      setRecommendationsHide(response.data);
      setLoadedRecommendations(true);
    } else {
      console.error('Failed to fetch recommendations:', response.message);
    }
  }

  async function loadSubscriptionCheck() {
    let res = await checkSubscriptionStatus();

    if (res.success) {
      setHasSubscription(res.data);
      setLoadedSubscriptionCheck(true);
    }
  }

  async function fetchAnalyticsData() {
    const response = await getDashboardAnalytics();
    if (response.success) {
      setAnalyticsData(response.data);
      setCompanyClicks(response.data.company_clicks);
      setCompanyViews(response.data.company_views);
      setTableData(
        response.data.company_table_data.map(row => ({
          ...row,
          view_count: parseInt(row.view_count, 10),
          click_count: parseInt(row.click_count, 10),
          impression_count: parseInt(row.impression_count, 10),
          year: row.year // Add year from date
        }))
      );

      setTableDataDaily(
        response.data.company_table_data_daily.map(row => ({
          ...row,
          view_count: parseInt(row.view_count, 10),
          click_count: parseInt(row.click_count, 10),
          impression_count: parseInt(row.impression_count, 10),
          year: row.year // Add year from date
        }))
      );

      setLoading(false);
    } else {
      console.error('Failed to fetch analytics data:', response.message);
    }
  }

  async function handlePublish() {
    setIsPublishing(true);

    let res = await publishCompany();

    if (res.success) {
      setIsPublishing(false);
      setPublishComplete(true);
      setShowPublishSuccess(true); // Show success dialog
    }

    else {
      setIsPublishing(false);
      console.error('Failed to publish company:', res.message);
    }
  }

  useEffect(() => {
    const fetchOfferData = async () => {
      try {
        let res = await getCompanyOffer();
        if (res.success && res.data != null && res.data.length > 0) {
          let row = res.data[0];
          setHasOffer(true);
        }

        else{
          setHasOffer(false);
        }

        setLoadedOffer(true);

      } catch (error) {
        console.error("Error fetching offer data:", error);
      }
    }

    async function fetchLeadCards() {
      const response = await getLeadFormSubmissionsForDashboard();
      if (response.success) {
        setLeadCards(response.data);
        setLoadedLeadCards(true);
      } else {
        console.error('Failed to fetch lead cards:', response.message);
      }
    }

    async function fetchUserCompany() {
      const response = await getUserCompany();
      if (response.success) {
        setUserCompany(response.data);
        setLoadingCompany(false);

        let data = response.data;
        if (data.description == null || data.logo == null || data.description == '' || data.logo == '') {
          setShowProfileNudge(true);
        }

        else {
          setShowProfileNudge(false);
        }

        console.log(response.data);
      }

      else {
        console.error('Failed to fetch user company:', response.message);
      }
    }

    async function fetchCompanyLaunches() {
      const response = await getCompanyLaunches();
      if (response.success) {
        setLaunches(response.data);
        setLoadingLaunches(false);
      } else {
        console.error('Failed to fetch company launches:', response.message);
      }
    }

    async function fetchLaunchCheck() {
      let res = await checkHasActiveLaunch();
      if (res.success) {
        setHasActiveLaunch(res.data);
      } else {
        console.error('Failed to check active launch:', res.message);
      }
    }

    async function fetchRecommendationsSent() {
      const response = await getSellerRecommendationsSent();
      if (response.success) {
        setRecommendationsSent(response.data);
        setLoadedRecommendations(true);
      } else {
        console.error('Failed to fetch recommendations:', response.message);
      }
    }

    async function fetchRecommendationsHide() {
      const response = await getSellerRecommendationsHide();
      if (response.success) {
        setRecommendationsHide(response.data);
        setLoadedRecommendations(true);
      } else {
        console.error('Failed to fetch recommendations:', response.message);
      }
    }

    async function fetchRecommendations() {
      const response = await getSellerRecommendationsForDashboard();
      if (response.success) {
        setRecommendations(response.data);
        setLoadedRecommendations(true);
      } else {
        console.error('Failed to fetch recommendations:', response.message);
      }
    }

    async function fetchAnalyticsData() {
      const response = await getDashboardAnalytics();
      if (response.success) {
        setAnalyticsData(response.data);
        setCompanyClicks(response.data.company_clicks);
        setCompanyViews(response.data.company_views);
        setTableData(
          response.data.company_table_data.map(row => ({
            ...row,
            view_count: parseInt(row.view_count, 10),
            click_count: parseInt(row.click_count, 10),
            impression_count: parseInt(row.impression_count, 10),
            year: row.year // Add year from date
          }))
        );

        setTableDataDaily(
          response.data.company_table_data_daily.map(row => ({
            ...row,
            view_count: parseInt(row.view_count, 10),
            click_count: parseInt(row.click_count, 10),
            impression_count: parseInt(row.impression_count, 10),
            year: row.year // Add year from date
          }))
        );

        setLoading(false);
      } else {
        console.error('Failed to fetch analytics data:', response.message);
      }
    }

    async function loadSubscriptionCheck() {
      let res = await checkSubscriptionStatus();

      if (res.success) {
        setHasSubscription(res.data);
        setLoadedSubscriptionCheck(true);
      }
    }

    fetchLeadCards();
    fetchCompanyLaunches();
    fetchLaunchCheck();
    fetchAnalyticsData();
    fetchRecommendations();
    fetchRecommendationsSent();
    fetchRecommendationsHide();
    fetchUserCompany();
    loadSubscriptionCheck();
    fetchOfferData();
  }, []);

  const handleExport = (data, filename) => {
    exportToCSV(data, filename);
  };

  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);

  const ConfirmDialog = ({ isOpen, onClose, onConfirm, lead }) => {
    if (!isOpen || !lead) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-purple-500 mr-2" />
              <h3 className="text-xl font-semibold">Confirm Invitation</h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Send className="w-5 h-5 text-purple-500 mt-1" />
              </div>
              <div>
                <p className="text-gray-700">
                  Send a demo invitation to <span className="font-semibold">{lead.first_name} {lead.last_name}</span> at <span className="font-semibold">{lead.company_name}</span>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {/* This will use 1 credit from your account. */}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-700 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onConfirm(lead);
                onClose();
              }}
              className="px-6 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 font-medium transition-colors inline-flex items-center"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Confirm & Send
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleInviteToDemo = (lead) => {
    if (!hasSubscription) {
      setIsUpgradeDialogOpen(true);
      return;
    }

    setSelectedInvite(lead);
    setIsConfirmDialogOpen(true);
  };

  async function handleBuyerNoMatch(id) {
    setLoadingNoFit(id);
    let res = await addSellerRecommendationStatus(id, 'Hide', 'Seller dashboard recommendation');

    if (res.success) {
      // Update recommendations and hide lists
      fetchRecommendations();
      fetchRecommendationsHide();
      fetchAnalyticsData();

      // Also remove from companyClicks
      setCompanyClicks(prev => prev.filter(click => click.registered_user_id !== id));
    }
    setLoadingNoFit(null);
  }

  async function handleConfirmInvite(lead) {
    try {
      // Add your API call here to send the intro
      // const response = await sendIntro(lead.id);
      let res = await addSellerRecommendationStatus(lead.registered_user_id, 'Approved', 'Seller dashboard recommendation');

      if (res.success) {
        let data2 = { buyer_id: lead.registered_user_id, credit_cost: 5, message: 'Booked through dashboard recommendations' }
        await createBuyerDemo(data2);
      }

      setSuccessMessage(`Introduction request sent successfully to ${lead.first_name} ${lead.last_name}`);
      setIsSuccessDialogOpen(true);

      fetchAnalyticsData();
      setAvailableCredits(prev => prev - 1);
      fetchRecommendations();
      fetchRecommendationsSent();
    } catch (error) {
      console.error('Failed to send intro:', error);
    }
  }

  async function handleSendMessage(registered_user_id) {
    let data2 = { buyer_id: registered_user_id, credit_cost: 5, message: 'Booked through dashboard lead form' }
    let res = await createBuyerDemo(data2);

    if (res.success) {
      navigate('/dashboard/message/' + res.data);
    }
  }

  const ProfileDialog = ({ isOpen, onClose, profile }) => {
    if (!isOpen || !profile) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg w-full max-w-2xl mx-4">
          <div className="flex justify-between items-center p-6 border-b">
            <h3 className="text-lg font-semibold">Profile Details</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-4">User Information</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-500">Name</label>
                    <p className="font-medium">{profile.first_name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Source</label>
                    <p className="font-medium">{profile.source}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Date</label>
                    <p className="font-medium">{new Date(profile.date_created).toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-4">Company Information</h4>
                <div className="space-y-3">
                  <div className="mb-4">
                    <img
                      src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + profile.logo}
                      alt="Company logo"
                      className="w-16 h-16 object-contain"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Company Name</label>
                    <p className="font-medium">{profile.buyer_company_name}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-6 border-t bg-gray-50 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const LeadDetailsDialog = ({ isOpen, onClose, lead }) => {
    if (!isOpen || !lead) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg w-full max-w-2xl mx-4">
          <div className="flex justify-between items-center p-6 border-b">
            <h3 className="text-lg font-semibold">Lead Form Details</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-4">Contact Information</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-500">Full Name</label>
                    <p className="font-medium">{lead.display_name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Job Title</label>
                    <p className="font-medium">{lead.job_title}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Company</label>
                    <p className="font-medium">{lead.company_name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Reference ID</label>
                    <p className="font-medium">#{lead.reference_id}</p>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-4">Lead Details</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-500">Status</label>
                    <p className="font-medium">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${lead.status === 'Approved' ? 'bg-green-100 text-green-800' :
                        lead.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                        {lead.status}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Date Submitted</label>
                    <p className="font-medium">{formatDate(lead.date_created)}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Message</label>
                    <p className="text-sm text-gray-600 mt-1">{lead.request_body || 'No message provided'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Close
            </button>

            {lead.registered_user_id != null && (
              <button
                onClick={() => {
                  // TODO: Implement message sending functionality
                  handleSendMessage(lead.registered_user_id);
                  console.log('Send message to:', lead);
                }}
                className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 inline-flex items-center"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Message
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const DeleteConfirmDialog = ({ isOpen, onClose, onConfirm, lead }) => {
    if (!isOpen || !lead) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <h3 className="text-xl font-semibold">Delete Lead</h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-gray-600 mb-6">
            Are you sure you want to delete this lead? This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-700 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onConfirm(lead);
                onClose();
              }}
              className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 font-medium transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleDeleteLead = async (lead) => {
    try {
      // TODO: Implement your delete API call here
      // const response = await deleteLead(lead.id);
      let res = await deleteLeadFormSubmission(lead.lead_form_submission_id);
      // If successful, update the leads list
      setLeadCards(prevLeads => prevLeads.filter(l => l.lead_form_submission_id !== lead.lead_form_submission_id));

      setSuccessMessage('Lead successfully deleted');
      setIsSuccessDialogOpen(true);
    } catch (error) {
      console.error('Failed to delete lead:', error);
    }
  };

  const BlurredUpgradeField = ({ children }) => (
    <div className="relative">
      <div className="filter blur-sm pointer-events-none">
        {children}
      </div>
      <div
        className="absolute inset-0 flex items-center justify-center cursor-pointer"
        onClick={() => setIsUpgradeDialogOpen(true)}
      >
        <div className="bg-purple-100 text-purple-600 px-2 py-1 rounded-md text-xs font-medium flex items-center min-w-[140px]">
          <Lock className="w-3 h-3 mr-1" />
          Upgrade to view
        </div>
      </div>
    </div>
  );

  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);

  const UpgradeDialog = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Upgrade Required</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-gray-600 mb-6">
            Upgrade your account to invite potential buyers to product demos. This feature is available only for premium subscribers.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-700 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // TODO: Add upgrade flow navigation
                navigate('/subscription');
                onClose();
              }}
              className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    );
  };

  const CreditsInfoDialog = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">About Credits</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="text-gray-600 space-y-4">
            <p>Credits are used to send introduction requests to potential leads.</p>
            <p>Each time you send an intro request, 1 credit is deducted from your available balance.</p>
            <p>You can get more credits by upgrading your plan or purchasing additional credit packs.</p>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const SuccessDialog = ({ isOpen, onClose, message }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-500" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-center mb-2">Success!</h3>
          <p className="text-gray-600 text-center mb-6">{message}</p>
          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const PublishSuccessDialog = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-xl w-full max-w-md mx-4 p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Seller Features Activated!</h3>
            <p className="text-sm text-gray-500 mb-6">
              Your company can now receive demo requests from potential buyers. Head over to your seller dashboard to start managing opportunities.
            </p>
            <button
              onClick={() => {
                onClose();
                navigate('/dashboard/seller');
              }}
              className="w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg hover:from-purple-700 hover:to-pink-600 transition-all duration-200"
            >
              Go to Seller Dashboard →
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ProspectSearchDialog = ({ isOpen, onClose }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-xl w-full max-w-lg mx-4 p-8">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-emerald-100 rounded-lg p-3">
                <Search className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Prospect Search</h3>
                <p className="text-sm text-gray-500 mt-1">Find your perfect customers</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-emerald-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-emerald-900 mb-3">Feature Highlights</h4>
            <ul className="space-y-2">
              <li className="flex items-center text-sm text-emerald-800">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2"></div>
                Advanced search filters by industry, company size & tech stack
              </li>
              <li className="flex items-center text-sm text-emerald-800">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2"></div>
                Access to verified decision-maker contact information
              </li>
              <li className="flex items-center text-sm text-emerald-800">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2"></div>
                Real-time buyer intent signals and engagement tracking
              </li>
              <li className="flex items-center text-sm text-emerald-800">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2"></div>
                Save searches and get automatic matches
              </li>
            </ul>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              onClick={() => navigate('/buyer-directory')}
              className="px-4 py-2 text-gray-600 hover:text-gray-700 font-medium"
            >
              Check It Out
            </button>
            <button
              onClick={() => {
                navigate('/subscription');
                onClose();
              }}
              className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 transform hover:scale-105 transition-all duration-200 shadow-md"
            >
              Upgrade Now →
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark">
      <ScrollToTop />
      <WelcomeDialog isOpen={showWelcome} onClose={handleCloseWelcome} />
      <div className="container-custom py-6 px-4 mx-auto">

        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">Performance Dashboard</h1>
              {!subscriberLoading && subscriberData != null && subscriberData.length > 0 && (
                <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                  {subscriberData[0].name}
                </div>
              )}

              {!subscriberLoading && (subscriberData == null || subscriberData.length == 0) && (
                <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                  Free Plan
                </div>
              )}

            </div>
            <p className="text-gray-600 mt-2">Track and analyze your lead generation performance</p>
          </div>
        </div>

        {launches && launches.length > 0 && launches.filter(x => (x.status == 'Running' || x.status == 'Pending')).length > 0 && <ActiveLaunchBanner launches={launches} />}

        {analyticsData?.pending_demos > 0 && <PendingDemoBanner count={analyticsData.pending_demos} />}

        {/* Add banner after the header, before other content */}
        {userCompany && !userCompany.is_seller && showSellerBanner && (
          <EnableSellerBanner
            onEnable={handlePublish}
            isPublishing={isPublishing}
            onDismiss={handleDismissSellerBanner}
          />
        )}

        {/* Add after other banners, before the stats cards */}
        {!subscriberLoading && (subscriberData == null || subscriberData.length == 0) && showBoostBanner && (
          <div className="mb-2 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-lg">
              <div className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="bg-white/10 rounded-full p-3">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-white font-semibold text-lg">Boost Your Visibility</h3>
                    </div>
                    <p className="text-white/80 text-sm mt-1">
                      Get featured in our weekly newsletter and reach thousands of potential buyers
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Link
                    to="/boost-visibility"
                    className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <TrendingUp className="h-4 w-4" />
                    <span>Learn More</span>
                  </Link>
                  <button
                    onClick={handleDismissBoost}
                    className="bg-white/10 hover:bg-white/20 text-white/80 hover:text-white px-3 py-2 rounded-lg transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add after boost banner, before offer banner */}
        {!subscriberLoading && (subscriberData == null || subscriberData.length == 0) && showSearchBanner && (
          <div className="mb-2 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-lg shadow-lg">
              <div className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="bg-white/10 rounded-full p-3">
                    <Search className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-white font-semibold text-lg">Find Your Ideal Prospects</h3>
                    </div>
                    <p className="text-white/80 text-sm mt-1">
                      Access our database of qualified B2B buyers and filter by industry, size & more
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setIsProspectSearchDialogOpen(true)}
                    className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <Search className="h-4 w-4" />
                    <span>Learn More</span>
                  </button>
                  <button
                    onClick={handleDismissSearch}
                    className="bg-white/10 hover:bg-white/20 text-white/80 hover:text-white px-3 py-2 rounded-lg transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {loadedOffer == true && hasOffer == false && showOfferBanner == true && (
          <div className="mb-2 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg">
              <div className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="bg-white/10 rounded-full p-3">
                    <Gift className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-white font-semibold text-lg">Create Your First Offer</h3>
                    </div>
                    <p className="text-white/80 text-sm mt-1">
                      Boost engagement by creating a special offer for potential buyers
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">

                  <Link
                    to="/dashboard/offers"
                    className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <Plus className="h-5 w-5 text-white" />
                    <span>Create Offer</span>
                  </Link>

                  <button
                    onClick={handleDismissOffer}
                    className="bg-white/10 hover:bg-white/20 text-white/80 hover:text-white px-3 py-2 rounded-lg transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Total Leads Card */}
          <div className="rounded-lg border bg-white shadow-sm p-6">
            <div className="pb-2">
              <h3 className="text-sm font-medium text-gray-500 ">Impressions</h3>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <UsersIcon className="w-8 h-8 text-purple-500" />
                <div>
                  <div className="text-2xl font-bold">{animatedImpressions}</div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">all time</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-white shadow-sm p-6">
            <div className="pb-2">
              <h3 className="text-sm font-medium text-gray-500 ">Profile Views</h3>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <UsersIcon className="w-8 h-8 text-purple-500" />
                <div>
                  <div className="text-2xl font-bold">{animatedPageViews}</div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">all time</p>
                </div>
              </div>
            </div>
          </div>

          {/* Conversion Rate Card */}
          <div className="rounded-lg border bg-white dark:bg-dark shadow-sm p-6">
            <div className="pb-2">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Engagement</h3>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-8 h-8 text-purple-500" />
                <div>
                  <div className="text-2xl font-bold">
                    {animatedPageClicks}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">all time</p>
                </div>
              </div>

            </div>
          </div>

          {/* Conversions Card */}
          {/* <div className="rounded-lg border bg-white dark:bg-dark shadow-sm p-6">
            <div className="pb-2">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Meeting Requests</h3>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <ChartBarIcon className="w-8 h-8 text-purple-500" />
                <div>
                  <div className="text-2xl font-bold">{analyticsData?.demo_count}</div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    all time
                  </p>
                </div>
              </div>

            </div>
          </div> */}

        </div>

        <div className="mb-8">
          <div className="border-b mb-4 flex items-center justify-between">
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'overview'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                Overview
              </button>

              <button
                onClick={() => setActiveTab('launches')}
                className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'launches'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <RocketIcon className="mr-2 h-4 w-4" />
                Launches
                {launches.length > 0 && (
                  <span className="ml-2 bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full text-xs font-medium">
                    {launches.length}
                  </span>
                )}
              </button>

              <button
                onClick={() => setActiveTab('leads')}
                className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'leads'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <Users className="mr-2 h-4 w-4" />
                Lead Forms
                {leadCards.length > 0 && (
                  <span className="ml-2 bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full text-xs font-medium">
                    {leadCards.length}
                  </span>
                )}
              </button>

              {/* <button
                onClick={() => setActiveTab('newsletter')}
                className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'newsletter'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <Mail className="mr-2 h-4 w-4" />
                Newsletter Outreach
              </button> */}
              {/* <button
                onClick={() => setActiveTab('sources')}
                className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'sources'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <PieChartIcon className="mr-2 h-4 w-4" />
                Sources
              </button> */}
            </div>
          </div>

          {activeTab === 'overview' && (
            <div className="rounded-lg border bg-white dark:bg-dark shadow-sm relative">
              <div className="flex items-center justify-between absolute right-2 top-4">
                <div className="flex items-center space-x-4 mr-4">
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewType('monthly')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${viewType === 'monthly'
                        ? 'bg-white text-purple-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setViewType('daily')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${viewType === 'daily'
                        ? 'bg-white text-purple-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                      Daily
                    </button>
                  </div>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="bg-white border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value={2025}>2025</option>
                    <option value={2024}>2024</option>
                    <option value={2023}>2023</option>
                    <option value={2022}>2022</option>
                  </select>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-lg font-semibold">Lead Performance</h3>
                <p className="text-sm text-gray-500">Lead generation and conversion trends over time</p>
              </div>
              <div className="p-6 pt-0 h-[400px] text-[12px]">
                {loading === false && (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={viewType === 'monthly'
                        ? tableData.filter(item => item.year == selectedYear)
                        : tableDataDaily.filter(item => item.year == selectedYear)
                      }
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey={viewType === 'monthly' ? 'month' : 'day_month'}
                        tick={{ fontSize: 12 }}
                        interval={viewType === 'monthly' ? 0 : 'preserveStartEnd'}
                        tickFormatter={(value) => {
                          if (viewType === 'monthly') return value;
                          const day = value.split('-')[0];
                          const month = value.split('-')[1];
                          return `${month}/${day}`;
                        }}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend
                        formatter={(value) => {
                          const labelMap = {
                            view_count: 'Views',
                            click_count: 'Clicks',
                            impression_count: 'Impressions',
                          };
                          return labelMap[value] || value;
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="view_count"
                        stroke="#8B5CF6"
                        activeDot={{ r: 8 }}
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="click_count"
                        stroke="#D946EF"
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="impression_count"
                        stroke="#F97316"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}

          {activeTab === 'newsletter' && (
            <div className="rounded-lg border bg-white shadow-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold">Newsletter Performance</h3>
                <p className="text-sm text-gray-500">Track the performance of your product features in our newsletter</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 pt-0">
                {/* Table Section */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-4 font-medium">Date</th>
                        <th className="text-left p-4 font-medium">Newsletter</th>
                        <th className="text-right p-4 font-medium">Total Reach</th>
                        <th className="text-right p-4 font-medium">Views</th>
                        <th className="text-right p-4 font-medium">Clicks</th>
                        <th className="text-right p-4 font-medium">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newsletterData.map((newsletter) => (
                        <tr key={newsletter.id} className="border-b last:border-b-0 hover:bg-gray-50">
                          <td className="p-4">{new Date(newsletter.date).toLocaleDateString()}</td>
                          <td className="p-4 font-medium">{newsletter.name}</td>
                          <td className="p-4 text-right">{newsletter.totalReach.toLocaleString()}</td>
                          <td className="p-4 text-right">{newsletter.views.toLocaleString()}</td>
                          <td className="p-4 text-right text-purple-600 font-medium">
                            {newsletter.clicks.toLocaleString()}
                          </td>
                          <td className="p-4 text-right font-medium">{newsletter.ctr}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pie Chart Section */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-4">Traffic Sources</h4>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={newsletterSourceData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {newsletterSourceData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend verticalAlign="bottom" height={36} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'leads' && (
            <div className="rounded-lg border bg-white dark:bg-dark shadow-sm">
              {(hasSubscription == true || hasSubscription == false) ? (
                <>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold">Lead Form Submissions</h3>
                    <p className="text-sm text-gray-500">Users expressing interest in your company.</p>
                  </div>
                  <div className="p-6 pt-0">
                    <div className="overflow-x-auto">
                      {loadedLeadCards ? (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-4 font-medium">Date</th>
                              <th className="text-left p-4 font-medium">Name</th>
                              <th className="text-left p-4 font-medium">Company</th>
                              <th className="text-left p-4 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leadCards.length > 0 ? (
                              leadCards.map((lead) => (
                                <tr key={lead.id} className="border-b last:border-b-0">
                                  <td className="p-4 font-medium">{formatDate(lead.date_created)}</td>

                                  <td className="p-4 font-medium">
                                    <div>
                                      <div className="items-center">
                                        <span className="text-xs text-gray-500">#{lead.reference_id}</span>

                                        <div>{lead.display_name}</div>

                                      </div>
                                      <div className="text-sm text-gray-500 mt-0.5">{lead.job_title}</div>
                                    </div>
                                  </td>
                                  <td className="p-4">{lead.company_name}</td>



                                  <td className="p-4">
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => {
                                          setSelectedLead(lead);
                                          setIsLeadDetailsOpen(true);
                                        }}
                                        className="inline-flex items-center px-3 py-1.5 bg-purple-500 text-white rounded-md hover:bg-purple-600 text-xs font-medium transition-colors"
                                      >
                                        View Details
                                      </button>
                                      <button
                                        onClick={() => {
                                          setLeadToDelete(lead);
                                          setIsDeleteConfirmOpen(true);
                                        }}
                                        className="inline-flex items-center px-3 py-1.5 bg-red-100 text-red-600 rounded-md hover:bg-red-200 text-xs font-medium transition-colors"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center py-8 text-gray-500">
                                  No active leads at this time
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      ) : (
                        <div className="flex justify-center py-8">
                          <svg className="animate-spin h-8 w-8 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="bg-purple-50 rounded-full p-3 mb-4">
                    <Lock className="h-8 w-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Unlock Advanced Lead Management
                  </h3>
                  <p className="text-gray-500 text-center mb-6 max-w-md">
                    Upgrade your account to access detailed lead tracking, management tools, and analytics.
                  </p>
                  <button
                    onClick={() => navigate('/subscription')}
                    className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                  >
                    Upgrade Now
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'launches' && (
            <div className="rounded-lg border bg-white dark:bg-dark shadow-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold">Leadrboard Launches</h3>
                <p className="text-sm text-gray-500">Your performance in weekly product launches</p>
              </div>
              <div className="p-6 pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">

                    <thead>
                      {loadingLaunches == false && launches.length > 0 && (
                        <tr className="border-b">
                          <th className="text-left p-4 font-medium">Week</th>
                          <th className="text-left p-4 font-medium">Status</th>
                          <th className="text-left p-4 font-medium">Start</th>
                          <th className="text-left p-4 font-medium">End</th>
                          <th className="text-left p-4 font-medium">Rank</th>
                          <th className="text-left p-4 font-medium">Actions</th>
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {loadingLaunches == false && launches.length > 0 ? (
                        launches.map((launch) => (
                          <tr key={launch.id} className="border-b last:border-b-0">
                            <td className="p-4 font-medium">{launch.name}</td>
                            <td className="p-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${launch.status === 'Running'
                                ? 'bg-green-100 text-green-800'
                                : launch.status === 'Completed'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-800'
                                }`}>
                                {launch.status === 'Running' ? (
                                  <>
                                    <PlayCircle className="w-3 h-3 mr-1" />
                                    {launch.status}
                                  </>
                                ) : launch.status === 'Completed' ? (
                                  <>
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    {launch.status}
                                  </>
                                ) : (
                                  launch.status
                                )}
                              </span>
                            </td>
                            <td className="p-4">{formatDate(launch.launch_date_start)}</td>
                            <td className="p-4">{formatDate(launch.launch_date_end)}</td>

                            <td className="p-4">
                              <div className="flex items-center">
                                {parseInt(launch.position) < 4 && (
                                  <Trophy className="w-4 h-4 text-yellow-400 mr-1" />
                                )}

                                <span>
                                  <i>{launch.position}</i>
                                </span>
                              </div>
                            </td>

                            <td className="p-4">
                              <button
                                onClick={() => navigate(`/dashboard/launch/manage/${launch.company_launch_id}`)}
                                className="inline-flex items-center px-3 py-1.5 bg-purple-500 text-white rounded-md hover:bg-purple-600 text-xs font-medium transition-colors"
                              >
                                Manage
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6">
                            <div className="flex flex-col items-center justify-center py-12 px-4">
                              <div className="bg-purple-50 rounded-full p-3 mb-4">
                                <RocketIcon className="h-8 w-8 text-purple-600" />
                              </div>
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                No Launch History
                              </h3>
                              <p className="text-gray-500 text-center mb-6 max-w-md">
                                Participate in weekly product launches to increase visibility and generate more leads for your business.
                              </p>
                              <Link
                                to="/launch/submit-product"
                                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                              >
                                <RocketIcon className="h-4 w-4 mr-2" />
                                Sign Up for Next Launch
                              </Link>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sources' && (
            <div className="rounded-lg border bg-white dark:bg-dark shadow-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold">Lead Sources</h3>
                <p className="text-sm text-gray-500">Distribution of leads by source</p>
              </div>
              <div className="p-6 pt-0 h-[400px] flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={leadsBySource}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={150}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {leadsBySource.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>


        {/* Add new Recommended Leads section */}
        <div className="rounded-lg border bg-white dark:bg-dark shadow-sm mb-8">
          <div className="p-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Recommended Leads</h3>
              <p className="text-sm text-gray-500">Potential leads that match your business profile</p>
            </div>

            {/* <div className="flex items-center space-x-2">
              <div className="text-sm flex items-center">
                <span className="text-gray-500">Available Credits:</span>
                <span className="ml-2 font-semibold text-purple-600">{availableCredits}</span>
                <button
                  onClick={() => setIsCreditsInfoOpen(true)}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>

            </div> */}
          </div>

          <div className="border-b px-6">
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveLeadsTab('available')}
                className={`px-4 py-2 border-b-2 ${activeLeadsTab === 'available'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Available to Send
              </button>
              <button
                onClick={() => setActiveLeadsTab('sent')}
                className={`px-4 py-2 border-b-2 ${activeLeadsTab === 'sent'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Already Sent
              </button>

              <button
                onClick={() => setActiveLeadsTab('notsent')}
                className={`px-4 py-2 border-b-2 ${activeLeadsTab === 'notsent'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Not a Fit
              </button>
            </div>
          </div>

          <div className="p-6 pt-0">
            <div className="w-full overflow-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="border-b">
                    <th></th>
                    <th className="text-left p-4 font-medium">Name</th>
                    <th className="text-left p-4 font-medium">Company</th>
                    <th className="text-left p-4 font-medium">Title</th>
                    <th className="text-left p-4 font-medium">Industry</th>
                    <th className="text-left p-4 font-medium">Size</th>
                    {activeLeadsTab === 'sent' && (
                      <th className="text-left p-4 font-medium">Status</th>
                    )}
                    {(activeLeadsTab === 'available' || activeLeadsTab === 'notsent') && (
                      <th className="text-left p-4 font-medium">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(activeLeadsTab === 'available'
                    ? recommendations
                    : activeLeadsTab === 'sent'
                      ? recommendationsSent
                      : recommendationsHide)
                    .slice(0, recommendationsDisplayCount)
                    .map((lead, index) => (
                      <tr
                        key={index}
                        className="border-b last:border-b-0 hover:bg-gray-50 transition-colors duration-200 ease-in-out cursor-pointer"
                        onClick={() => {
                          setSelectedProfile(lead);
                          setIsProfileDialogOpen(false);
                        }}
                      >
                        <td>
                          <div className='dashboard__table-company-logo'>
                            <img
                              src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + lead.logo}
                              alt={lead.company_name}
                            />
                          </div>
                        </td>
                        <td className="p-4 font-medium">{`${lead.first_name} ${lead.last_name}`}</td>
                        <td className="p-4">{lead.company_name}</td>
                        <td className="p-4">{lead.job_title}</td>
                        <td className="p-4">{lead.industry}</td>
                        <td className="p-4">{lead.company_size}</td>

                        {activeLeadsTab === 'sent' && (
                          <td className="p-4">
                            <span className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium min-w-[120px] ${(lead.demo_status === 'Approved' || lead.company_views > 0) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>

                              {lead.buyer_status == 'Approved' && lead.seller_status == 'Pending' && (
                                <span>
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Demo Requested
                                </span>
                              )}

                              {(lead.buyer_status == 'Declined' || lead.seller_status == 'Declined') && (
                                <span>
                                  <X className="w-3 h-3 mr-1" /> Declined
                                </span>
                              )}

                              {lead.buyer_status == 'Approved' && lead.seller_status == 'Approved' && (
                                <span>
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Demo Scheduled
                                </span>
                              )}

                              {lead.buyer_status == 'Pending' && (
                                <span>
                                  <Clock className="w-3 h-3 mr-1" /> Pending
                                </span>
                              )}

                              {lead.buyer_status == null && lead.company_views > 0 && (
                                <>
                                  Viewed Profile
                                </>
                              )}

                              {lead.buyer_status == null && lead.company_views == 0 && (
                                <>
                                  <Clock className="w-3 h-3 mr-1" /> {lead.demo_status || 'Pending'}
                                </>
                              )}
                            </span>
                          </td>
                        )}

                        {(activeLeadsTab === 'available' || activeLeadsTab === 'notsent') && (
                          <td className="p-4">
                            <div className="flex space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleInviteToDemo(lead);
                                }}
                                className="inline-flex items-center px-3 py-1.5 bg-purple-500 text-white rounded-md hover:bg-purple-600 text-xs font-medium transition-colors min-w-[110px]"
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Send Intro
                              </button>
                              {activeLeadsTab === 'available' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBuyerNoMatch(lead.registered_user_id);
                                  }}
                                  disabled={loadingNoFit === lead.registered_user_id}
                                  className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 text-xs font-medium transition-colors min-w-[110px] disabled:opacity-50"
                                >
                                  {loadingNoFit === lead.registered_user_id ? (
                                    <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  ) : (
                                    <X className="w-3 h-3 mr-1" />
                                  )}
                                  Not a fit
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}

                  {(activeLeadsTab === 'available'
                    ? recommendations
                    : activeLeadsTab === 'sent'
                      ? recommendationsSent
                      : recommendationsHide).length === 0 && (
                      <tr>
                        <td colSpan={activeLeadsTab === 'available' ? 7 : 6} className="text-center py-8 text-gray-500">
                          {activeLeadsTab === 'available'
                            ? 'No recommendations available at this time'
                            : activeLeadsTab === 'sent'
                              ? 'No invitations sent yet'
                              : 'No leads marked as not a fit'}
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
              <ProfileDialog
                isOpen={isProfileDialogOpen}
                onClose={() => setIsProfileDialogOpen(false)}
                profile={selectedProfile}
              />

              {activeLeadsTab == 'available' && (
                <div className="text-center mt-4">
                  {hasSubscription ? (
                    <Link
                      to="/dashboard/search"
                      className="block px-4 py-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      View More Recommendations
                    </Link>
                  ) : (
                    <button
                      onClick={() => setIsUpgradeDialogOpen(true)}
                      className="inline-flex items-center px-4 py-2 text-sm text-gray-500 hover:text-purple-600 font-medium group"
                    >
                      <Lock className="w-4 h-4 mr-2 text-gray-400 group-hover:text-purple-500" />
                      Upgrade to View More Recommendations
                    </button>
                  )}
                </div>
              )}

              {(activeLeadsTab !== 'available' &&
                (activeLeadsTab === 'sent'
                  ? recommendationsSent
                  : recommendationsHide).length > recommendationsDisplayCount) && (
                  <div className="text-center mt-4">
                    <button
                      onClick={() => setRecommendationsDisplayCount(prev => prev + ITEMS_PER_PAGE)}
                      className="px-4 py-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      View More Recommendations
                    </button>
                  </div>
                )}

            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-white dark:bg-dark shadow-sm mb-8">
          <div className="p-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Top Engaging Leads</h3>
              <p className="text-sm text-gray-500">Users who have engaged with your company profile page.</p>
            </div>
            <button
              onClick={() => handleExport(companyClicks, 'company-clicks')}
              className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
            >
              Export CSV
            </button>
          </div>
          <div className="p-6 pt-0">
            <div className="w-full overflow-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="border-b">
                    <th></th>
                    <th className="text-left p-4 font-medium">Job Title</th>
                    <th className="text-left p-4 font-medium">Company</th>
                    <th className="text-left p-4 font-medium">Industry</th>
                    <th className="text-left p-4 font-medium">Department</th>
                    <th className="text-left p-4 font-medium">Interest</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedCompanyClicks.map((view, index) => (
                    <tr
                      key={index}
                      className="border-b last:border-b-0 hover:bg-gray-50 transition-colors duration-200 ease-in-out cursor-pointer"
                    >
                      <td>
                        <div className='dashboard__table-company-logo'>
                          <img src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + view.logo} />
                        </div>
                      </td>

                      <td className="p-4 font-medium">{view.job_title}</td>

                      <td className="p-4">
                        {!hasSubscription ? (
                          <BlurredUpgradeField>
                            {view.buyer_company_name}
                          </BlurredUpgradeField>
                        ) : (
                          view.buyer_company_name
                        )}
                      </td>

                      <td className="p-4">{view.industry || '-'}</td>
                      <td className="p-4">{view.department || '-'}</td>

                      <td className="p-4">
                        {getInterestBadge(view.click_count)}
                      </td>

                      {/* <td className="p-4">{new Date(view.date_created).toLocaleDateString()}</td> */}
                      {/* <td className="p-4">{view.source}</td> */}
                      <td className="p-4">
                        {view.buyer_status === 'Approved' || view.buyer_status === 'Declined' ||
                          view.seller_status === 'Approved' || view.seller_status === 'Declined' ? (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs min-w-[100px] font-medium ${view.buyer_status === 'Approved' || view.seller_status === 'Approved' ?
                            'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                            }`}>
                            {(view.buyer_status === 'Approved' && view.seller_status == 'Pending') && (
                              <span>
                                <Check className='w-3 h-3 mr-1' /> Demo Requested
                              </span>
                            )}

                            {(view.buyer_status === 'Rescheduled' || view.seller_status == 'Rescheduled') && (
                              <span>
                                <Clock2 className='w-3 h-3 mr-1' /> Rescheduling
                              </span>
                            )}

                            {(view.buyer_status === 'Approved' && view.seller_status == 'Approved') && (
                              <span>
                                <Check className='w-3 h-3 mr-1' /> Demo Scheduled
                              </span>
                            )}

                            {(view.seller_status === 'Approved' && view.buyer_status == 'Pending') && (
                              <span>
                                <Check className='w-3 h-3 mr-1' /> Intro Sent
                              </span>
                            )}

                            {(view.seller_status === 'Declined' || view.buyer_status == 'Declined') && (
                              <span>
                                <X className='w-3 h-3 mr-1' /> Declined
                              </span>
                            )}
                          </span>
                        ) : view.status === null ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleInviteToDemo(view);
                              }}
                              className="inline-flex items-center px-3 py-1.5 bg-purple-500 text-white rounded-md hover:bg-purple-600 text-xs font-medium transition-colors min-w-[110px]"
                            >
                              <Send className="w-3 h-3 mr-1" />
                              Send Intro
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBuyerNoMatch(view.registered_user_id);
                              }}
                              disabled={loadingNoFit === view.registered_user_id}
                              className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 text-xs font-medium transition-colors min-w-[110px] disabled:opacity-50"
                            >
                              {loadingNoFit === view.registered_user_id ? (
                                <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <X className="w-3 h-3 mr-1" />
                              )}
                              Not a fit
                            </button>
                          </div>
                        ) : (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${view.status === 'Approved' ? 'bg-green-100 text-green-800' :
                            view.status === 'Hide' ? 'bg-gray-100 text-gray-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                            {view.status === 'Approved' && (
                              <span>
                                <Check className='w-3 h-3 mr-1' /> Intro Sent
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ProfileDialog
                isOpen={isProfileDialogOpen}
                onClose={() => setIsProfileDialogOpen(false)}
                profile={selectedProfile}
              />
              {companyClicksDisplayCount < companyClicks.length && (
                <div className="text-center mt-4">
                  <button
                    onClick={() => handleLoadMore('companyClicks')}
                    className="px-4 py-2 text-sm text-purple-600 hover:text-purple-700 font-medium inline-flex items-center"
                  >
                    {!hasSubscription ? 'Upgrade to View More' : 'Load More'}
                    {!hasSubscription && <Lock className="w-3 h-3 ml-1" />}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-white shadow-sm mb-8">
          <div className="relative">
            <div className={``}>
              <div className="p-6 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold">Recent Company Views</h3>
                  <p className="text-sm text-gray-500">Check out who's interested in your company.</p>
                </div>
                <button
                  onClick={() => handleExport(companyViews, 'company-views')}
                  className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
                >
                  Export CSV
                </button>
              </div>
              <div className="p-6 pt-0">
                <div className="w-full overflow-auto">
                  <table className="w-full text-[14px]">
                    <thead>
                      <tr className="border-b">
                        <th>  </th>
                        <th className="text-left p-4 font-medium">Job Title</th>
                        <th className="text-left p-4 font-medium">Company</th>
                        <th className="text-left p-4 font-medium">Industry</th>
                        <th className="text-left p-4 font-medium">Department</th>
                        <th className="text-left p-4 font-medium">Interest</th>
                        {/* <th className="text-left p-4 font-medium">Date</th> */}
                        {/* <th className="text-left p-4 font-medium">Source</th> */}
                        <th className="text-left p-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedCompanyViews.map((click, index) => (
                        <tr
                          key={index}
                          className="border-b last:border-b-0 hover:bg-gray-50 transition-colors duration-200 ease-in-out cursor-pointer"
                        >
                          <td>
                            <div className='dashboard__table-company-logo'>
                              <img src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + click.logo} />
                            </div>
                          </td>

                          <td className="p-4 font-medium">{click.job_title}</td>

                          <td className="p-4">
                            {!hasSubscription ? (
                              <BlurredUpgradeField>
                                {click.buyer_company_name}
                              </BlurredUpgradeField>
                            ) : (
                              click.buyer_company_name
                            )}
                          </td>
                          <td className="p-4">{click.industry || '-'}</td>
                          <td className="p-4">{click.department || '-'}</td>

                          <td className="p-4">
                            {getInterestBadge(click.view_count)}
                          </td>


                          <td className="p-4">
                            {click.buyer_status === 'Approved' || click.buyer_status === 'Declined' ||
                              click.seller_status === 'Approved' || click.seller_status === 'Declined' ? (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs min-w-[100px] font-medium ${click.buyer_status === 'Approved' || click.seller_status === 'Approved' ?
                                'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                                }`}>
                                {(click.buyer_status === 'Approved' && click.seller_status == 'Pending') && (
                                  <span>
                                    <Check className='w-3 h-3 mr-1' /> Demo Requested
                                  </span>
                                )}

                                {(click.buyer_status === 'Rescheduled' || click.seller_status == 'Rescheduled') && (
                                  <span>
                                    <Clock2 className='w-3 h-3 mr-1' /> Rescheduling
                                  </span>
                                )}

                                {(click.buyer_status === 'Approved' && click.seller_status == 'Approved') && (
                                  <span>
                                    <Check className='w-3 h-3 mr-1' /> Demo Scheduled
                                  </span>
                                )}

                                {(click.seller_status === 'Approved' && click.buyer_status == 'Pending') && (
                                  <span>
                                    <Check className='w-3 h-3 mr-1' /> Intro Sent
                                  </span>
                                )}

                                {(click.seller_status === 'Declined' || click.buyer_status == 'Declined') && (
                                  <span>
                                    <X className='w-3 h-3 mr-1' /> Declined
                                  </span>
                                )}
                              </span>
                            ) : click.status === null ? (
                              <div className="flex space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleInviteToDemo(click);
                                  }}
                                  className="inline-flex items-center px-3 py-1.5 bg-purple-500 text-white rounded-md hover:bg-purple-600 text-xs font-medium transition-colors min-w-[110px]"
                                >
                                  <Send className="w-3 h-3 mr-1" />
                                  Send Intro
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBuyerNoMatch(click.registered_user_id);
                                  }}
                                  disabled={loadingNoFit === click.registered_user_id}
                                  className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 text-xs font-medium transition-colors min-w-[110px] disabled:opacity-50"
                                >
                                  {loadingNoFit === click.registered_user_id ? (
                                    <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                  ) : (
                                    <X className="w-3 h-3 mr-1" />
                                  )}
                                  Not a fit
                                </button>
                              </div>
                            ) : (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs min-w-[100px] font-medium ${click.status === 'Approved' ? 'bg-green-100 text-green-800' :
                                click.status === 'Hide' ? 'bg-gray-100 text-gray-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                {click.status == 'Approved' && (
                                  <span>
                                    <Check className='w-3 h-3 mr-1' /> Intro Sent
                                  </span>
                                )}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <ProfileDialog
                    isOpen={isProfileDialogOpen}
                    onClose={() => setIsProfileDialogOpen(false)}
                    profile={selectedProfile}
                  />
                  {companyViewsDisplayCount < companyViews.length && (
                    <div className="text-center mt-4">
                      <button
                        onClick={() => handleLoadMore('companyViews')}
                        className="px-4 py-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
                      >
                        {!hasSubscription ? 'Upgrade to View More' : 'Load More'}
                        {!hasSubscription && <Lock className="w-3 h-3 ml-1" />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>

      {!hasSubscription && (
        <div className=" left-0 right-0 bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white p-4 shadow-lg transform transition-transform duration-200 ease-in-out z-50">
          <div className="container mx-auto flex items-center justify-between px-4">
            <div className="flex items-center space-x-4">
              <div className="hidden md:block p-2 bg-white/10 rounded-lg">
                <Trophy className="w-6 h-6 text-yellow-300" />
              </div>
              <div>
                <h4 className="font-semibold text-lg">Unlock Premium Features</h4>
                <p className="text-white/80 text-sm">Get advanced access to leads, detailed analytics, and more!</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/subscription')}
              className="px-6 py-2 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors shadow-md flex items-center space-x-2"
            >
              <span>Upgrade Now</span>
              <ArrowUpIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={handleConfirmInvite}
        lead={selectedInvite}
      />
      <UpgradeDialog
        isOpen={isUpgradeDialogOpen}
        onClose={() => setIsUpgradeDialogOpen(false)}
      />
      <CreditsInfoDialog
        isOpen={isCreditsInfoOpen}
        onClose={() => setIsCreditsInfoOpen(false)}
      />
      <SuccessDialog
        isOpen={isSuccessDialogOpen}
        onClose={() => setIsSuccessDialogOpen(false)}
        message={successMessage}
      />
      <LeadDetailsDialog
        isOpen={isLeadDetailsOpen}
        onClose={() => setIsLeadDetailsOpen(false)}
        lead={selectedLead}
      />
      <DeleteConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteLead}
        lead={leadToDelete}
      />
      <PublishSuccessDialog
        isOpen={showPublishSuccess}
        onClose={() => setShowPublishSuccess(false)}
      />
      <ProspectSearchDialog 
        isOpen={isProspectSearchDialogOpen}  
        onClose={() => setIsProspectSearchDialogOpen(false)}
      />
    </div>
  );
};

export default DashboardAnalytics;